<template>
  <div data-testid="confirm-order-container" class="confirm-order__container">
    <div>
      <div class="confirm-instrument_heading">
        <IconButton
          data-testid="confirm-order-arrow-icon"
          class="confirm-instrument_heading__icon"
          icon-name="ic_arrow_left"
          @click="$emit('back-to-instrument')"
        />
        <p class="text-p6-semibold confirm-instrument_heading__text">
          Confirmação
        </p>
      </div>
      <div>
        <HeadingDisplay
          avatar-type="image"
          :title="instrumentTitle"
          :subtitle="description"
          :avatar-content="instrumentTitleAvatarName"
          avatar-alt=""
        />
      </div>
      <div>
        <ListDisplay
          class="confirm-order-unified-account__value"
          title="Quantidade"
          :value="instrumentQtd.toString()"
        />
        <ListDisplay
          class="confirm-order-unified-account__value"
          title="Melhor oferta"
          :value="displayValue(bestOffer)"
        />
        <ListDisplay
          class="confirm-order-unified-account__value"
          title="Total estimado"
          :value="displayValue(estimatedTotal)"
        />
        <ListDisplay
          class="confirm-order-unified-account__value"
          title="Taxa de corretagem"
          show-icon
          icon-name="EF0061"
          :value="brokerageFee"
          @click="toggleDynamicContentModal"
        />
      </div>
      <BaseText
        v-if="type === ConfirmOrderType.Buy"
        size="sm"
        :color="NebraskaColors.textSecondary"
      >
        Saldo disponível
        <strong>{{ balance }}</strong>
      </BaseText>
      <BaseText v-else size="sm" :color="NebraskaColors.textSecondary">
        Quantidade disponível
        <strong>{{ positionQuantity }} ativos</strong>
      </BaseText>
    </div>
    <div class="create-order_button" data-testid="confirm-create-order-button">
      <ButtonPrimary
        data-testid="confirm-order-button"
        :loading="loadingButton"
        :label="computedButtonLabel"
        @click="createOrder"
      />
    </div>
    <DynamicContentModal
      :is-open="isDynamicContentModalOpen"
      title="Taxa de corretagem"
      :modal-info="brokerageDisclaimer"
      :costs-button="true"
      @close="toggleDynamicContentModal"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  HeadingDisplay,
  ButtonPrimary,
  ListDisplay,
  IconButton,
  BaseText
} from '@warrenbrasil/nebraska-web';
import { moneyViewer as money } from '@/modules/common/helpers/currency';
import { OrderType, OrderTypesPrimary, OrderTypesSecondary } from '../Order';
import { InstrumentStatus } from '@/modules/trade/services/instruments';
import { PropType } from 'vue';
import { IDynamicContent } from '@/modules/common/types';
import DynamicContentModal from '@/modules/common/components/DynamicContentModal/DynamicContentModal.vue';

@Component({
  filters: { money },
  components: {
    HeadingDisplay,
    ButtonPrimary,
    ListDisplay,
    IconButton,
    BaseText,
    DynamicContentModal
  }
})
export default class ConfirmOrder extends Vue {
  @Prop({ type: Number, required: true })
  readonly type!: number;

  @Prop({ type: Number, required: true })
  readonly instrumentQtd!: number;

  @Prop({ type: String, required: true })
  readonly balance!: string;

  @Prop({ type: Number })
  readonly bestOffer!: number;

  @Prop({ type: Number })
  readonly estimatedTotal!: number;

  @Prop({ type: Boolean })
  readonly loadingButton!: boolean;

  @Prop({ type: String, required: true })
  readonly instrumentTitle!: string;

  @Prop({ type: String })
  readonly instrumentDescription!: string;

  @Prop({ type: Object, required: true })
  readonly instrument!: InstrumentStatus;

  @Prop({ type: Number, required: true })
  readonly positionQuantity!: number;

  @Prop({ type: String, default: '' })
  readonly description!: string;

  @Prop({ type: String, required: false })
  readonly brokerageFee!: string;

  @Prop({ type: Array as PropType<IDynamicContent[]>, required: false })
  readonly brokerageDisclaimer!: IDynamicContent[];

  readonly NebraskaColors = NebraskaColors;
  readonly ConfirmOrderType = OrderType;
  private isDynamicContentModalOpen = false;

  public toggleDynamicContentModal() {
    this.isDynamicContentModalOpen = !this.isDynamicContentModalOpen;
  }

  @Emit('request-order')
  public createOrder() {}

  get instrumentTitleAvatarName() {
    return this.instrumentTitle.replace(/\d[F]/g, '');
  }

  public displayValue(value: number) {
    return money(value);
  }

  get getTypeHeading() {
    return this.type === OrderType.Buy
      ? OrderTypesPrimary.Buy
      : OrderTypesPrimary.Sell;
  }

  get getTypeButton() {
    return this.type === OrderType.Buy
      ? OrderTypesSecondary.Buy
      : OrderTypesSecondary.Sell;
  }

  get computedButtonLabel() {
    return `${this.getTypeButton} ${this.instrument?.symbol}`;
  }
}
</script>

<style lang="less" scoped>
.confirm-order-unified-account {
  &__value {
    margin-top: @size-spacing-x300;
  }

  &__value:first-child {
    margin-top: @size-spacing-x600;
  }

  &__value:last-child {
    margin-bottom: @size-spacing-x500;
  }
}

.confirm-instrument_heading {
  margin-top: @size-spacing-x600;
  margin-bottom: @size-spacing-x500;
  position: relative;

  .confirm-instrument_heading__text {
    text-align: center;
    color: @text-primary;
  }

  .confirm-instrument_heading__icon {
    position: absolute;
    cursor: pointer;
    bottom: -3px;
  }
}

.confirm-instrument_balance {
  margin-top: 20px;
  color: @text-secondary;
  user-select: none;
}

.create-order_button {
  margin-top: @size-spacing-x400;
  button {
    width: 100%;
  }
}

.confirm-instrument_title {
  color: @text-secondary;
  user-select: none;
}

.confirm-instrument_value {
  color: @text-primary;
  user-select: none;
}

.trade-order__container {
  background-color: @background-secondary;
}

.create-order__button__text {
  color: @text-negative;
}

.confirm-order__container {
  padding: @size-spacing-x600 @size-spacing-x400 @size-spacing-x800
    @size-spacing-x400;
}

@media @notebookUp {
  .confirm-order__container {
    padding: 0 @size-spacing-x800 @size-spacing-x800 @size-spacing-x800;
  }
}
</style>
